/**
 * Beam weapons as camera-facing ribbons (Phase 8): one strip of quads drawn once per live beam,
 * each segment placed along the beam in the vertex shader and pushed sideways so the ribbon
 * always faces the camera, with a bright core and scrolling noise in the fragment. Per-beam
 * data (start, end, width, colour, intensity, phase) lives in small storage arrays read as
 * instanced attributes; the rules still decide every hit, this only draws the light.
 */
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  MeshBasicNodeMaterial,
  type Scene,
} from 'three/webgpu';
import {
  abs,
  attribute,
  cameraPosition,
  cross,
  float,
  instancedArray,
  mix,
  normalize,
  sin,
  time,
  type vec4,
} from 'three/tsl';

import { effectMrt } from './mrt.js';

const SEGMENTS = 16;
/** A beam is redrawn every simulation tick it stays on; it fades out this long after the last. */
const HOLD_SECONDS = 0.1;

interface StorageNode {
  value: { array: Float32Array; needsUpdate: boolean };
  toAttribute(): ReturnType<typeof vec4>;
}

interface Beam {
  key: number;
  shown: number;
}

/** A strip of `SEGMENTS` quads: `along` runs 0..1 down the beam, `side` is -1 or 1. */
function ribbonGeometry(): BufferGeometry {
  const along: number[] = [];
  const side: number[] = [];
  const index: number[] = [];
  for (let i = 0; i <= SEGMENTS; i++) {
    const t = i / SEGMENTS;
    along.push(t, t);
    side.push(-1, 1);
  }
  for (let i = 0; i < SEGMENTS; i++) {
    const a = i * 2;
    index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const geometry = new BufferGeometry();
  // A position attribute keeps every loader and bounds helper happy; the shader ignores it.
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(along.length * 3), 3));
  geometry.setAttribute('along', new BufferAttribute(new Float32Array(along), 1));
  geometry.setAttribute('side', new BufferAttribute(new Float32Array(side), 1));
  geometry.setIndex(index);
  return geometry;
}

export class BeamRibbons {
  readonly mesh: Mesh;
  private readonly beams: Beam[];
  private readonly a: StorageNode;
  private readonly b: StorageNode;
  private readonly c: StorageNode;
  private dirty = false;
  private readonly material: MeshBasicNodeMaterial;

  constructor(
    private readonly scene: Scene,
    readonly capacity = 4,
  ) {
    this.beams = Array.from({ length: capacity }, () => ({ key: -1, shown: -1 }));
    const wrap = (): StorageNode => instancedArray(capacity, 'vec4') as unknown as StorageNode;
    this.a = wrap(); // start.xyz, width
    this.b = wrap(); // end.xyz, intensity
    this.c = wrap(); // colour.rgb, phase
    this.material = this.buildMaterial();
    this.mesh = new Mesh(ribbonGeometry(), this.material);
    this.mesh.count = capacity;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 25;
    scene.add(this.mesh);
  }

  private buildMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    const a = this.a.toAttribute();
    const b = this.b.toAttribute();
    const c = this.c.toAttribute();
    const along = attribute<'float'>('along', 'float');
    const side = attribute<'float'>('side', 'float');
    const start = a.xyz;
    const end = b.xyz;
    const width = a.w;
    const p = mix(start, end, along);
    const dir = normalize(end.sub(start));
    const toCamera = normalize(cameraPosition.sub(p));
    const sideways = normalize(cross(dir, toCamera));
    // Slightly wider at the muzzle, tapering to the hit point.
    const taper = float(1.15).sub(along.mul(0.3));
    material.positionNode = p.add(sideways.mul(side).mul(width).mul(taper));
    const core = float(1.0).sub(abs(side));
    const ripple = float(0.7).add(sin(along.mul(28.0).sub(time.mul(22.0)).add(c.w)).mul(0.3));
    const glow = float(1.0).add(core.mul(core).mul(core).mul(3.0));
    material.colorNode = c.rgb.mul(glow).mul(ripple).mul(b.w);
    material.opacityNode = core.mul(0.6).add(0.4);
    material.transparent = true;
    material.blending = AdditiveBlending;
    material.depthWrite = false;
    material.side = DoubleSide;
    material.mrtNode = effectMrt();
    return material;
  }

  /** Draws (or refreshes) the beam `key` between two points this tick. */
  show(
    now: number,
    key: number,
    from: readonly number[],
    to: readonly number[],
    width: number,
    colour: { r: number; g: number; b: number },
    intensity = 1,
  ): void {
    let index = this.beams.findIndex((beam) => beam.key === key && now - beam.shown < HOLD_SECONDS);
    if (index < 0) {
      // A free ribbon, else the one shown longest ago.
      index = this.beams.reduce(
        (best, beam, i) => (beam.shown < this.beams[best]!.shown ? i : best),
        0,
      );
    }
    const beam = this.beams[index]!;
    beam.key = key;
    beam.shown = now;
    const o = index * 4;
    this.a.value.array.set([from[0] ?? 0, from[1] ?? 0, from[2] ?? 0, width], o);
    this.b.value.array.set([to[0] ?? 0, to[1] ?? 0, to[2] ?? 0, intensity], o);
    this.c.value.array.set([colour.r, colour.g, colour.b, key * 1.7], o);
    this.dirty = true;
  }

  /** Fades out ribbons whose beam stopped; uploads any change. */
  update(now: number): void {
    for (let i = 0; i < this.capacity; i++) {
      const beam = this.beams[i]!;
      if (beam.key < 0) continue;
      const age = now - beam.shown;
      if (age >= HOLD_SECONDS) {
        beam.key = -1;
        this.a.value.array[i * 4 + 3] = 0;
        this.dirty = true;
      } else if (age > 0) {
        this.b.value.array[i * 4 + 3] = Math.max(0, 1 - age / HOLD_SECONDS);
        this.dirty = true;
      }
    }
    if (this.dirty) {
      this.a.value.needsUpdate = true;
      this.b.value.needsUpdate = true;
      this.c.value.needsUpdate = true;
      this.dirty = false;
    }
  }

  /** Ribbons currently lit. */
  alive(now: number): number {
    return this.beams.filter((beam) => beam.key >= 0 && now - beam.shown < HOLD_SECONDS).length;
  }

  clear(): void {
    for (const beam of this.beams) beam.key = -1;
    this.a.value.array.fill(0);
    this.dirty = true;
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
